<template>
  <Teleport to="body">
    <TransitionRoot
      appear
      :show="visible"
      as="template"
    >
      <Dialog
        as="div"
        class="relative z-50"
        @close="$emit('close')"
      >
        <TransitionChild
          as="template"
          v-bind="backdrop"
        >
          <div class="fixed inset-0 bg-dark-gray bg-opacity-50" />
        </TransitionChild>

        <div
          v-if="visible"
          class="fixed inset-0 z-60 flex items-center justify-center bg-transparent"
        >
          <TransitionChild
            as="template"
            v-bind="transitions"
          >
            <DialogPanel
              class="relative z-10 w-full scroll-auto rounded-t-lg bg-white p-2"
              :class="{
                'mt-12 h-full': isFullScreen,
                [`mt-auto ${minHeight}`]: !isFullScreen,
                [`m-auto ${maxWidth} rounded-b-lg`]: !isMobile,
              }"
            >
              <div v-if="showCloseIcon">
                <img
                  class="bottom-sheet-close-icon absolute top-4 z-20 h-4 w-4 cursor-pointer"
                  :class="[
                    {
                      'right-5': closeIconRightAlign,
                      'left-5': !closeIconRightAlign,
                    },
                    closeIconClass,
                  ]"
                  src="@/assets/close-icon-gray.svg"
                  alt="close icon"
                  @click="$emit('close')"
                />
              </div>

              <div class="bottom-sheet-container h-full overflow-y-scroll pt-8">
                <slot />
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>
  </Teleport>
</template>

<script setup>
import { computed } from 'vue';
import {
  Dialog,
  DialogPanel,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue';
import { useScreenSize } from '@/composables';

defineProps({
  visible: Boolean,
  isFullScreen: Boolean,
  showCloseIcon: {
    type: Boolean,
    default: true,
  },
  minHeight: {
    type: String,
    default: 'min-h-[300px]',
  },
  maxWidth: {
    type: String,
    default: 'max-w-[600px]',
  },
  closeIconRightAlign: {
    type: Boolean,
    default: false,
  },
  closeIconClass: {
    type: String,
    default: '',
  },
});

defineEmits(['close']);

const { screenSize } = useScreenSize();

const isMobile = computed(() => screenSize.value === 'mobile');

const transitions = computed(() => {
  if (isMobile.value) {
    return {
      enter: 'transform transition ease-out duration-300',
      enterFrom: 'translate-y-full opacity-0',
      enterTo: 'translate-y-0 opacity-100',
      leave: 'transform transition ease-in duration-200',
      leaveFrom: 'translate-y-0 opacity-100',
      leaveTo: 'translate-y-full opacity-0',
    };
  } else {
    return {
      enter: 'transform transition ease-out duration-0',
      enterFrom: 'translate-y-full opacity-0',
      enterTo: 'translate-y-0 opacity-100',
      leave: 'transform transition ease-in duration-0',
      leaveFrom: 'translate-y-0 opacity-100',
      leaveTo: 'translate-y-full opacity-0',
    };
  }
});

const backdrop = computed(() => {
  if (isMobile.value) {
    return {
      enter: 'ease-out duration-200',
      enterFrom: 'opacity-0',
      enterTo: 'opacity-100',
      leave: 'ease-in duration-200',
      leaveFrom: 'opacity-100',
      leaveTo: 'opacity-0',
    };
  } else {
    return {
      enter: 'ease-out duration-0',
      enterFrom: 'opacity-0',
      enterTo: 'opacity-100',
      leave: 'ease-in duration-0',
      leaveFrom: 'opacity-100',
      leaveTo: 'opacity-0',
    };
  }
});
</script>
